import React, {useEffect, useMemo, useState} from 'react'
import {toast} from 'react-toastify'
import {
  createAdminAccessBundle,
  getAdminPermissionCatalog,
  getAdminAccessBundles,
  getAdminBundleDefaultRules,
  updateAdminBundlePermissions,
  replaceAdminBundleDefaultRules,
  getCrewRankPermissionMappings,
  updateCrewRankPermissionMapping,
  resetCrewRankPermissionMapping,
  type AdminAccessBundleDefaultRule,
  type AdminAccessBundle,
  type PermissionCatalogEntry,
  type CrewRankPermissionMapping,
} from '../core/accessRequests'
import {getCrewBaseRanksForPermissionDropdown, type CrewRankOption} from '../core/_requests'
import {GroupedPermissionCheckboxGrid} from './GroupedPermissionViews'
import SearchableSelect from '../../../shared/components/SearchableSelect'

const CREW_ROLE = 'CREW'
const NON_SCOPED_CREW_RANK_CODES = new Set([
  'READ_PERMISSION',
  'CREATE_PERMISSION',
  'UPDATE_PERMISSION',
  'DELETE_PERMISSION',
  'READ_ROLE_PERMISSION',
  'CREATE_ROLE_PERMISSION',
  'UPDATE_ROLE_PERMISSION',
  'DELETE_ROLE_PERMISSION',
  'READ_CREW_RANK_PERMISSION',
  'UPDATE_CREW_RANK_PERMISSION',
  'CREATE_ROLE',
  'UPDATE_ROLE',
  'DELETE_ROLE',
])
const normalizeCode = (v: string) => v.trim().toUpperCase()
const normalizeBaseRank = (v?: string | null) =>
  String(v ?? '')
    .trim()
    .replace(/\s+/g, ' ')
    .replace(/\s*-\s*\d+\s*$/, '')
    .trim()
const rankMatrixBundleCode = (rankId: number) => `CREW_RANK_${rankId}_DEFAULT_MATRIX`
const setsEqual = (a: Set<string>, b: Set<string>) => {
  if (a.size !== b.size) return false
  for (const x of Array.from(a)) {
    if (!b.has(x)) return false
  }
  return true
}

export interface CrewRankPermissionsManagerPanelProps {
  canEdit?: boolean
  actorIsSuperAdmin?: boolean
}

export const CrewRankPermissionsManagerPanel: React.FC<CrewRankPermissionsManagerPanelProps> = ({
  canEdit = true,
  actorIsSuperAdmin = false,
}) => {
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [ranks, setRanks] = useState<CrewRankOption[]>([])
  const [bundles, setBundles] = useState<AdminAccessBundle[]>([])
  const [defaultRules, setDefaultRules] = useState<AdminAccessBundleDefaultRule[]>([])
  const [catalog, setCatalog] = useState<PermissionCatalogEntry[]>([])
  const [selectedRankId, setSelectedRankId] = useState<number | null>(null)
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedCodes, setSelectedCodes] = useState<Set<string>>(new Set())
  const [scopedMappings, setScopedMappings] = useState<CrewRankPermissionMapping[]>([])

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    const load = async () => {
      const rankRows = await getCrewBaseRanksForPermissionDropdown()
      const catalogRows = await getAdminPermissionCatalog()
      const [bundleRows, rules, mappings] = actorIsSuperAdmin
        ? await Promise.all([
            getAdminAccessBundles(),
            getAdminBundleDefaultRules(),
            Promise.resolve([] as CrewRankPermissionMapping[]),
          ])
        : [[], [], await getCrewRankPermissionMappings()]
      if (cancelled) return
      {
        if (cancelled) return
        const sortedRanks = [...(rankRows || [])].sort((a, b) =>
          String(a.rank || '').localeCompare(String(b.rank || ''), undefined, {sensitivity: 'base'})
        )
        setRanks(sortedRanks)
        setBundles((bundleRows || []).filter((b) => b.active))
        setDefaultRules(rules || [])
        setCatalog(catalogRows || [])
        setScopedMappings(mappings || [])
        setSelectedRankId(sortedRanks?.[0]?.id ?? null)
      }
    }
    load()
      .catch((e: any) => {
        if (!cancelled) toast.error(e?.message || 'Failed to load crew rank defaults')
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [actorIsSuperAdmin])

  const selectedRank = useMemo(
    () => ranks.find((r) => Number(r.id) === Number(selectedRankId)) || null,
    [ranks, selectedRankId]
  )
  const selectedRankKey = useMemo(() => normalizeBaseRank(selectedRank?.rank), [selectedRank])

  const loadedCodesForSelectedRank = useMemo(() => {
    if (!actorIsSuperAdmin) {
      return new Set(
        scopedMappings.find((m) => Number(m.rankId) === Number(selectedRankId))?.permissionCodes ||
          []
      )
    }
    if (!selectedRankKey) return new Set<string>()
    const bundleIds = defaultRules
      .filter(
        (r) =>
          (r.roleType || '').toUpperCase() === CREW_ROLE &&
          normalizeBaseRank(r.rankLabel) === selectedRankKey
      )
      .map((r) => Number(r.bundleId))
      .filter((v) => Number.isFinite(v) && v > 0)
    const mappedCodes = new Set<string>()
    bundles
      .filter((b) => bundleIds.includes(Number(b.id)))
      .forEach((b) => {
        ;(b.permissions || []).forEach((p) => {
          if ((p.effect || 'ALLOW').toUpperCase() === 'ALLOW' && p.permissionCode) {
            mappedCodes.add(normalizeCode(p.permissionCode))
          }
        })
      })
    return mappedCodes
  }, [actorIsSuperAdmin, bundles, defaultRules, scopedMappings, selectedRankId, selectedRankKey])

  const selectedScopedMapping = useMemo(
    () =>
      scopedMappings.find((m) => Number(m.rankId) === Number(selectedRankId) && !m.inherited) ||
      null,
    [scopedMappings, selectedRankId]
  )

  useEffect(() => {
    setSelectedCodes(new Set(loadedCodesForSelectedRank))
  }, [loadedCodesForSelectedRank])

  const togglePermission = (code: string) => {
    const normalized = normalizeCode(code)
    setSelectedCodes((prev) => {
      const next = new Set(prev)
      if (next.has(normalized)) next.delete(normalized)
      else next.add(normalized)
      return next
    })
  }

  const setManyPermissions = (codes: string[], selected: boolean) => {
    setSelectedCodes((prev) => {
      const next = new Set(prev)
      codes.forEach((c) => {
        const n = normalizeCode(c)
        if (selected) next.add(n)
        else next.delete(n)
      })
      return next
    })
  }

  const handleSave = async () => {
    if (!selectedRankId || !selectedRankKey) {
      toast.error('Please select a crew rank.')
      return
    }
    setSaving(true)
    try {
      if (!actorIsSuperAdmin) {
        const scopedPermissionCodes = Array.from(selectedCodes).filter(
          (code) => !NON_SCOPED_CREW_RANK_CODES.has(normalizeCode(code))
        )
        const saved = await updateCrewRankPermissionMapping(
          Number(selectedRankId),
          scopedPermissionCodes
        )
        setScopedMappings((prev) => [
          ...prev.filter((m) => Number(m.rankId) !== Number(selectedRankId)),
          saved,
        ])
        toast.success('Crew rank permissions saved for your company group.')
        return
      }
      const rankId = Number(selectedRankId)
      const code = rankMatrixBundleCode(rankId)
      const existingBundle = bundles.find((b) => normalizeCode(b.code || '') === code)
      const targetBundle = existingBundle
        ? existingBundle
        : await createAdminAccessBundle({
            code,
            name: `${selectedRank?.rank || `Rank ${rankId}`} default permissions`,
            moduleHint: 'CREW',
            active: true,
          })

      await updateAdminBundlePermissions(
        targetBundle.id,
        Array.from(selectedCodes).map((permissionCode) => ({
          permissionCode,
          effect: 'ALLOW',
          defaultScope: null,
        }))
      )

      const untouched = defaultRules.filter(
        (r) =>
          !(
            (r.roleType || '').toUpperCase() === CREW_ROLE &&
            normalizeBaseRank(r.rankLabel) === selectedRankKey
          )
      )
      const mappedRows: AdminAccessBundleDefaultRule[] = [
        {
          roleType: CREW_ROLE,
          rankId,
          rankLabel: selectedRank?.rank || null,
          bundleId: targetBundle.id,
          sortOrder: 0,
          active: true,
        },
      ]
      const next = await replaceAdminBundleDefaultRules([...untouched, ...mappedRows])
      const refreshedBundles = await getAdminAccessBundles()
      setBundles((refreshedBundles || []).filter((b) => b.active))
      setDefaultRules(next || [])
      toast.success('Crew rank permissions saved.')
    } catch (e: any) {
      toast.error(e?.message || 'Failed to save crew rank permissions')
    } finally {
      setSaving(false)
    }
  }

  const handleResetToGlobal = async () => {
    if (actorIsSuperAdmin || !selectedRankId) return
    setSaving(true)
    try {
      await resetCrewRankPermissionMapping(Number(selectedRankId))
      setScopedMappings((prev) => prev.filter((m) => Number(m.rankId) !== Number(selectedRankId)))
      toast.success('Crew rank permissions reset to global defaults.')
    } catch (e: any) {
      toast.error(e?.message || 'Failed to reset crew rank permissions')
    } finally {
      setSaving(false)
    }
  }

  const selectedRuleBundleCount = useMemo(() => {
    if (!selectedRankKey) return 0
    return defaultRules.filter(
      (r) =>
        (r.roleType || '').toUpperCase() === CREW_ROLE &&
        normalizeBaseRank(r.rankLabel) === selectedRankKey
    ).length
  }, [defaultRules, selectedRankKey])
  const dirtyPermissions = useMemo(
    () => !setsEqual(selectedCodes, loadedCodesForSelectedRank),
    [selectedCodes, loadedCodesForSelectedRank]
  )

  return (
    <div className='w-100'>
      <div className='d-flex justify-content-between align-items-start mb-3'>
        <div>
          <h5 className='mb-1 text-dark'>Crew rank permissions</h5>
          <p className='text-muted small mb-0'>
            Select base crew ranks and assign permission codes. Suffix variants inherit from the
            same base rank.
          </p>
        </div>
        <button
          type='button'
          className='btn btn-primary'
          onClick={handleSave}
          disabled={!canEdit || saving || loading || !selectedRankId || !dirtyPermissions}
        >
          {saving ? 'Saving…' : 'Save permissions'}
        </button>
        {!actorIsSuperAdmin && selectedScopedMapping ? (
          <button
            type='button'
            className='btn btn-light'
            onClick={handleResetToGlobal}
            disabled={!canEdit || saving}
          >
            Reset to global default
          </button>
        ) : null}
      </div>
      {loading ? (
        <div className='text-muted small'>Loading crew base ranks and permission catalog…</div>
      ) : ranks.length === 0 ? (
        <div className='alert alert-warning mb-0'>
          No active crew ranks found. Add active ranks in Rank Management first.
        </div>
      ) : (
        <>
          <div className='row g-3 mb-3'>
            <div className='col-md-5'>
              <label className='form-label fw-semibold text-dark'>Base crew rank</label>
              <SearchableSelect
                className='form-select'
                value={selectedRankId ?? ''}
                onChange={(e) => setSelectedRankId(e.target.value ? Number(e.target.value) : null)}
              >
                {ranks.map((r) => (
                  <option key={r.id} value={r.id}>
                    {r.rank}
                  </option>
                ))}
              </SearchableSelect>
            </div>
            <div className='col-md-7'>
              <label className='form-label fw-semibold text-dark'>Search permissions</label>
              <input
                className='form-control'
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder='Search by module, menu, code, or action'
              />
            </div>
          </div>
          <div
            className='border rounded p-2 bg-light'
            style={{maxHeight: '60vh', overflowY: 'auto'}}
          >
            <GroupedPermissionCheckboxGrid
              catalog={catalog}
              searchQuery={searchQuery}
              selectedCodes={selectedCodes}
              disabled={!canEdit || !selectedRankId || saving}
              onToggle={togglePermission}
              countLegend='enabled'
              bulk={{
                totalCatalogCount: catalog.length,
                onSetMany: setManyPermissions,
                disabled: !canEdit || !selectedRankId || saving,
                globalLegend: 'enabled',
              }}
              startAllExpanded
            />
          </div>
          <div className='d-flex flex-wrap gap-4 mt-2 small text-muted'>
            <span>Selected base rank: {selectedRank?.rank || '—'}</span>
            <span>Enabled permissions: {selectedCodes.size}</span>
            <span>Mapped bundle rules: {selectedRuleBundleCount}</span>
            <span>{dirtyPermissions ? 'Unsaved changes' : 'No pending changes'}</span>
            {!actorIsSuperAdmin &&
            !selectedScopedMapping &&
            scopedMappings.some(
              (m) => Number(m.rankId) === Number(selectedRankId) && m.inherited
            ) ? (
              <span>Inherited from global defaults</span>
            ) : null}
          </div>
        </>
      )}
    </div>
  )
}
